import { Modal, Form, Input, InputNumber, Select, TreeSelect, Radio, DatePicker, App } from 'antd'
import { useState, useEffect } from 'react'
import dayjs, { type Dayjs } from 'dayjs'
import { submitTenantRequest, updateTenantRequest } from '../services/tenant-request-api'
import { getPartners, type Partner } from '../services/partner-api'
import { useFeatureFlagSelect } from '../../tenants/hooks/useFeatureFlagSelect'
import { type TenantLicense, lookupTenants } from '../../tenants/services/tenant-api'
import type { Tenant } from '../../tenants/types/tenant.types'
import { useAuth } from '../../../auth/AuthProvider'
import { getAllowedWorkspaceTypes } from '../../../utils/roles'
import type { TenantRequest } from '../types/tenant-request.types'

interface RequestTenantModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  editRequest?: TenantRequest | null
}

// 2-character tenant code suffix used when auto-deriving from a 3-char prefix.
// Must stay in lockstep with Host.Data.Constants.WorkspaceType.TENANT_CODE_SUFFIX.
const TENANT_CODE_SUFFIX: Record<'new' | 'demo' | 'trial' | 'uat', string> = {
  new: '01',
  demo: 'dm',
  trial: 'tr',
  uat: 'ua'
}

interface RequestTenantFormValues {
  tenantCode?: string
  tenantCodePrefix?: string
  workspaceCode: string
  workspaceName: string
  partnerCode?: string
  ownerEmail: string
  restoreFromTemplate: string | null
  workspaceType: 'new' | 'demo' | 'trial' | 'uat'
  copyFromWorkspaceCode?: string
  deactivationDate?: Dayjs | null
  deactivationReason?: string
  featureFlags: string[]
  notes?: string
  // License counts
  empCount?: number
  empPsgCount?: number
  empLveCount?: number
  empClaCount?: number
  empAttCount?: number
  empPfmCount?: number
}

export function RequestTenantModal({ open, onClose, onSuccess, editRequest }: RequestTenantModalProps) {
  const { user } = useAuth()
  const { message } = App.useApp()
  const [form] = Form.useForm<RequestTenantFormValues>()
  const { treeData } = useFeatureFlagSelect()
  const [loading, setLoading] = useState(false)
  const [partners, setPartners] = useState<Partner[]>([])
  const [loadingPartners, setLoadingPartners] = useState(false)
  const [workspaces, setWorkspaces] = useState<Tenant[]>([])

  const isEditMode = !!editRequest

  // Fetch partners when modal opens
  useEffect(() => {
    if (open) {
      setLoadingPartners(true)
      getPartners()
        .then(setPartners)
        .catch(err => {
          console.error('Failed to fetch partners:', err)
          setPartners([])
        })
        .finally(() => setLoadingPartners(false))
    }
  }, [open])

  // Fetch existing workspaces (active only) with feature flags for the UAT copy-from picker
  useEffect(() => {
    if (open) {
      lookupTenants({ includeFeatureFlags: true })
        .then(response => setWorkspaces(response.data))
        .catch(err => {
          console.error('Failed to fetch workspaces:', err)
          setWorkspaces([])
        })
    }
  }, [open])

  // Populate form when editing or set default email when creating
  useEffect(() => {
    if (open) {
      if (editRequest) {
        // Edit mode: populate with existing data
        const license: TenantLicense = editRequest.licenseJsonb ? JSON.parse(editRequest.licenseJsonb) : {}
        form.setFieldsValue({
          tenantCode: editRequest.tenantCode,
          tenantCodePrefix: editRequest.tenantCodePrefix || undefined,
          workspaceCode: editRequest.workspaceCode,
          workspaceName: editRequest.workspaceName,
          partnerCode: editRequest.partnerCode || undefined,
          ownerEmail: editRequest.ownerEmail,
          restoreFromTemplate: editRequest.restoreFromTemplate,
          workspaceType: editRequest.workspaceType,
          copyFromWorkspaceCode: editRequest.copyFromWorkspaceCode || undefined,
          deactivationDate: editRequest.deactivationDate ? dayjs(editRequest.deactivationDate) : null,
          deactivationReason: editRequest.deactivationReason || undefined,
          featureFlags: editRequest.featureFlags || [],
          notes: editRequest.notes || undefined,
          empCount: license.EmpCount,
          empPsgCount: license.EmpPsgCount,
          empLveCount: license.EmpLveCount,
          empClaCount: license.EmpClaCount,
          empAttCount: license.EmpAttCount,
          empPfmCount: license.EmpPfmCount
        })
      }
    }
  }, [open, editRequest, user, form])

  const handleSubmit = async (values: RequestTenantFormValues) => {
    try {
      setLoading(true)

      const license: TenantLicense = {
        EmpCount: values.empCount,
        EmpPsgCount: values.empPsgCount,
        EmpLveCount: values.empLveCount,
        EmpClaCount: values.empClaCount,
        EmpAttCount: values.empAttCount,
        EmpPfmCount: values.empPfmCount
      }
      const hasLicense = Object.values(license).some(v => v !== undefined && v !== null)

      const restoreFromTemplate =
        values.workspaceType === 'demo' || values.workspaceType === 'trial'
          ? 'zea02'
          : values.workspaceType === 'new' || values.workspaceType === 'uat'
            ? 'zea03'
            : null

      const requestData = {
        tenantCode: values.tenantCode?.toLowerCase() || undefined,
        tenantCodePrefix: values.tenantCodePrefix?.toLowerCase() || undefined,
        workspaceCode: values.workspaceCode.toLowerCase(),
        workspaceName: values.workspaceName,
        partnerCode: values.partnerCode || undefined,
        ownerEmail: values.ownerEmail.toLowerCase(),
        restoreFromTemplate,
        workspaceType: values.workspaceType,
        copyFromWorkspaceCode: values.copyFromWorkspaceCode?.toUpperCase() || null,
        deactivationDate: values.deactivationDate ? values.deactivationDate.format('YYYY-MM-DD') : null,
        deactivationReason: values.deactivationReason || null,
        featureFlags: values.featureFlags || [],
        licenseJsonb: hasLicense ? JSON.stringify(license) : undefined,
        notes: values.notes || undefined
      }

      if (isEditMode && editRequest) {
        await updateTenantRequest(editRequest.id, requestData)
        message.success('Workspace request updated successfully')
      } else {
        await submitTenantRequest(requestData)
        message.success('Workspace request submitted successfully')
      }

      form.resetFields()
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error(`Failed to ${isEditMode ? 'update' : 'submit'} workspace request:`, error)

      // Handle validation errors from API response
      const responseData = error.response?.data
      if (responseData?.errors && typeof responseData.errors === 'object') {
        // Map API validation errors to form fields
        const fieldErrors = Object.entries(responseData.errors).map(([field, messages]) => ({
          name: field as keyof RequestTenantFormValues,
          errors: Array.isArray(messages) ? messages : [messages]
        }))
        form.setFields(fieldErrors)
      }
      const errorMessage =
        responseData?.error?.message ||
        responseData?.ErrorMessage ||
        responseData?.message ||
        `Failed to ${isEditMode ? 'update' : 'submit'} workspace request`
      message.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const partnerOptions = partners.map(partner => ({
    label: partner.partnerName || partner.partnerCode,
    value: partner.partnerCode
  }))

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      title={isEditMode ? 'Edit workspace request' : 'Request new workspace'}
      open={open}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={900}
      okText={isEditMode ? 'Update request' : 'Submit request'}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          restoreFromTemplate: 'zea03',
          workspaceType: 'new',
          featureFlags: []
        }}
      >
        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.workspaceType !== cur.workspaceType}>
          {({ getFieldValue, setFieldValue }) => {
            const allowedTypes = getAllowedWorkspaceTypes(user)
            const currentType = getFieldValue('workspaceType') as 'new' | 'demo' | 'trial' | 'uat'
            if (currentType && !allowedTypes.has(currentType) && allowedTypes.size > 0) {
              const firstAllowed = Array.from(allowedTypes)[0]
              setFieldValue('workspaceType', firstAllowed)
            }
            return (
              <Form.Item
                label="Workspace type"
                name="workspaceType"
                rules={[{ required: true, message: 'Please select workspace type' }]}
              >
                <Radio.Group>
                  {allowedTypes.has('new') && <Radio value="new">New workspace</Radio>}
                  {allowedTypes.has('demo') && <Radio value="demo">Demo workspace</Radio>}
                  {allowedTypes.has('trial') && <Radio value="trial">Trial workspace</Radio>}
                  {allowedTypes.has('uat') && <Radio value="uat">UAT workspace</Radio>}
                </Radio.Group>
              </Form.Item>
            )
          }}
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.workspaceType !== cur.workspaceType}>
          {({ getFieldValue }) => {
            const workspaceType = getFieldValue('workspaceType') as string
            return workspaceType === 'uat' ? (
              <Form.Item
                label="Copy from workspace"
                name="copyFromWorkspaceCode"
                rules={[{ required: true, message: 'Source workspace is required for UAT' }]}
                extra="Selecting a workspace will copy its enabled feature flags into this request"
              >
                <Select
                  showSearch
                  placeholder="Select source workspace"
                  options={workspaces.map(w => ({
                    label: `${w.tenantName} (${w.workspaceCode})`,
                    value: w.workspaceCode
                  }))}
                  filterOption={(input, option) =>
                    ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={(value: string) => {
                    const source = workspaces.find(w => w.workspaceCode === value)
                    if (source) {
                      const enabledFlags = source.featureFlags
                        .filter(ff => ff.disabledDate === null)
                        .map(ff => ff.featureFlag)
                      form.setFieldValue('featureFlags', enabledFlags)
                    }
                  }}
                />
              </Form.Item>
            ) : null
          }}
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.workspaceType !== cur.workspaceType}>
          {({ getFieldValue }) => {
            const workspaceType = getFieldValue('workspaceType') as string
            const isTrialOrUat = workspaceType === 'trial' || workspaceType === 'uat'
            const defaultDate =
              workspaceType === 'trial'
                ? dayjs().add(21, 'day')
                : workspaceType === 'uat'
                  ? dayjs().add(1, 'month')
                  : null
            return isTrialOrUat ? (
              <Form.Item
                label="Deactivation date"
                name="deactivationDate"
                initialValue={defaultDate}
                rules={[{ required: true, message: 'Deactivation date is required for Trial/UAT' }]}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            ) : null
          }}
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) =>
            prev.workspaceType !== cur.workspaceType || prev.deactivationDate !== cur.deactivationDate
          }
        >
          {({ getFieldValue }) => {
            const workspaceType = getFieldValue('workspaceType') as string
            const deactivationDate = getFieldValue('deactivationDate') as Dayjs | null
            if (workspaceType !== 'trial' && workspaceType !== 'uat') return null
            const defaultDate = workspaceType === 'trial' ? dayjs().add(21, 'day') : dayjs().add(1, 'month')
            const needsReason = deactivationDate && deactivationDate.isAfter(defaultDate, 'day')
            return needsReason ? (
              <Form.Item
                label="Deactivation reason"
                name="deactivationReason"
                rules={[{ required: true, message: 'Reason is required when deactivation date exceeds the default' }]}
              >
                <Input.TextArea rows={2} maxLength={1000} placeholder="Reason for extended deactivation date" />
              </Form.Item>
            ) : null
          }}
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item label="Partner" name="partnerCode" extra="Optional partner identifier">
            <Select
              placeholder="Select a partner"
              options={partnerOptions}
              loading={loadingPartners}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item
            label="Workspace name"
            name="workspaceName"
            rules={[
              { required: true, message: 'Please enter workspace name' },
              { max: 200, message: 'Workspace name must be 200 characters or less' }
            ]}
          >
            <Input placeholder="e.g., Zealys Pte Ltd" />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            label="Workspace code"
            name="workspaceCode"
            normalize={value => value?.toUpperCase()}
            rules={[
              { required: true, message: 'Please enter workspace code' },
              { max: 50, message: 'Workspace code must be 50 characters or less' },
              { pattern: /^[A-Z0-9_-]+$/, message: 'Only uppercase letters, numbers, hyphens and underscores allowed' }
            ]}
            extra="Short display code (uppercase)"
          >
            <Input placeholder="e.g., ZEALYS" />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) =>
              prev.tenantCode !== cur.tenantCode ||
              prev.tenantCodePrefix !== cur.tenantCodePrefix ||
              prev.workspaceType !== cur.workspaceType ||
              prev.partnerCode !== cur.partnerCode
            }
          >
            {({ getFieldValue }) => {
              const tenantCode = getFieldValue('tenantCode') as string | undefined
              // Once an explicit tenantCode is set, prefix is irrelevant — backend uses tenantCode as-is.
              // But in edit mode, always show prefix so user can modify it.
              if (tenantCode && !isEditMode) return null

              const prefix = (getFieldValue('tenantCodePrefix') as string | undefined)?.toLowerCase() || ''
              const workspaceType = getFieldValue('workspaceType') as 'new' | 'demo' | 'trial' | 'uat'
              const partnerCode = getFieldValue('partnerCode') as string | undefined

              let preview = ''
              if (partnerCode) {
                const partnerPrefix = partnerCode.slice(0, 2).toLowerCase()
                if (workspaceType === 'new') {
                  preview = `${partnerPrefix}### (running number auto-assigned)`
                } else if (workspaceType) {
                  preview = `${partnerPrefix}${TENANT_CODE_SUFFIX[workspaceType]}# (running number auto-assigned)`
                }
              } else if (/^[a-z0-9]{3}$/.test(prefix) && workspaceType) {
                preview = prefix + TENANT_CODE_SUFFIX[workspaceType]
              }

              return (
                <Form.Item
                  label="Tenant code prefix"
                  name="tenantCodePrefix"
                  normalize={value => value?.toLowerCase()}
                  rules={[
                    { len: 3, message: 'Prefix must be exactly 3 characters' },
                    { pattern: /^[a-z0-9]{3}$/, message: 'Only lowercase letters and numbers' }
                  ]}
                  extra={
                    partnerCode
                      ? 'Ignored for partner workspaces (derived from partner code)'
                      : preview
                        ? `Will be derived as: ${preview}`
                        : 'Enter 3 chars; suffix is added based on workspace type'
                  }
                >
                  <Input placeholder="e.g., zea" maxLength={3} disabled={!!partnerCode} />
                </Form.Item>
              )
            }}
          </Form.Item>
        </div>

        <Form.Item
          label="Owner email"
          name="ownerEmail"
          rules={[
            { required: true, message: 'Please enter owner email' },
            { type: 'email', message: 'Please enter a valid email' }
          ]}
          extra="Email of the workspace owner"
        >
          <Input placeholder="e.g., owner@zealys.com" type="email" />
        </Form.Item>

        <Form.Item
          label="Notes"
          name="notes"
          rules={[{ max: 1000, message: 'Notes must be 1000 characters or less' }]}
          extra="Optional notes for this request"
        >
          <Input.TextArea rows={2} placeholder="Add any notes or comments about this request" maxLength={1000} />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.featureFlags !== currentValues.featureFlags}
        >
          {({ getFieldValue }) => {
            const featureFlags: string[] = getFieldValue('featureFlags') || []
            const hasFlag = (flag: string) => featureFlags.includes(flag)

            return (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'start' }}>
                <Form.Item label="Feature flags" name="featureFlags" style={{ marginBottom: 0 }}>
                  <TreeSelect
                    treeCheckable
                    treeData={treeData}
                    placeholder="Select feature flags"
                    treeDefaultExpandAll
                    allowClear
                    showCheckedStrategy={TreeSelect.SHOW_ALL}
                  />
                </Form.Item>
                <Form.Item label="License" style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '80px 1fr 130px 1fr',
                      gap: '4px 8px',
                      alignItems: 'center'
                    }}
                  >
                    <span>People</span>
                    <Form.Item name="empCount" noStyle>
                      <InputNumber min={0} placeholder="0" size="small" style={{ width: '100%' }} />
                    </Form.Item>
                    <span>Payroll</span>
                    <Form.Item name="empPsgCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('payroll')}
                      />
                    </Form.Item>
                    <span>Leave</span>
                    <Form.Item name="empLveCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('leave')}
                      />
                    </Form.Item>
                    <span>Claim</span>
                    <Form.Item name="empClaCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('claim')}
                      />
                    </Form.Item>
                    <span>Attendance</span>
                    <Form.Item name="empAttCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('attendance')}
                      />
                    </Form.Item>
                    <span>Performance review</span>
                    <Form.Item name="empPfmCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('performance')}
                      />
                    </Form.Item>
                  </div>
                </Form.Item>
              </div>
            )
          }}
        </Form.Item>
      </Form>
    </Modal>
  )
}
